import React, { useState } from 'react'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { FormattedMessage } from 'react-intl'
import messages from '../../../../messages'
import SequenceEntryItem from './SequenceEntryItem'
import SequenceList from './SequenceList'
import { ConfigContext } from '../../Genomics'
import GlobalAlert from '@src/components/GlobalAlert'
import DataTable from '@src/routes/Configurators/components/DataTable'
import clsx from 'clsx'
import { ManualSearchProps } from '@src/routes/Configurators/components/ConfiguratorTypes'

const useStyles = makeStyles((theme: Theme) => {
  return {
    errorAlert: {
      width: 'fit-content',
      marginTop: theme.spacing(6),
    },
    sequwnceListHeader: {
      margin: theme.spacing(10, 0, 6, 0),
      fontSize: theme.typography.pxToRem(20),
      fontWeight: theme.typography.fontWeightBold,
      textTransform: 'uppercase',
    },
    sequenceEntery: {
      fontWeight: theme.typography.fontWeightBold,
      fontSize: theme.typography.pxToRem(20),
      marginTop: theme.spacing(8),
      marginBottom: theme.spacing(1),
      textTransform: 'uppercase',
      '&$hasErrorMessages': {
        marginTop: theme.spacing(6),
      },
      borderBottom: `1px solid ${theme.palette.grey[400]}`,
    },
    requiredField: {
      fontSize: theme.typography.pxToRem(12),
      marginBottom: theme.spacing(6),
    },
    hasErrorMessages: {},
    startTypingMsg: {
      marginBottom: theme.spacing(1),
    },
  }
})

const ManualSearch = ({
  productDetails,
  setErrorMsg,
  errorMessages,
  instructions,
}: ManualSearchProps) => {
  const classes = useStyles()
  const { state, dispatch } = React.useContext(ConfigContext)
  const { sessionData, crisprSequenceList } = state
  const [disable, setDisable] = useState(false)

  React.useEffect(() => {
    if (sessionData[0].listSubModels?.length) {
      const listLength = sessionData[0].listSubModels?.length
      if (
        sessionData[0].listSubModels[listLength - 1].name !== '' &&
        sessionData[0].listSubModels[listLength - 1].sequence !== ''
      ) {
        //if last item is not an empty object
        //id of the last valid item in the list is fetched
        const lastItemId = sessionData[0].listSubModels[listLength - 1].id
        dispatch({
          type: 'UPDATE_CRISPR_CURRENT_INDEX',
          data: lastItemId,
        })
      } else {
        //only one empty object in list
        if (listLength === 1) {
          dispatch({
            type: 'UPDATE_CRISPR_CURRENT_INDEX',
            data: 1,
          })
          dispatch({
            type: 'CRISPR_SEQUENCE_LIST',
            data: false,
          })
        } else {
          //if valid items and an empty obj is there in list
          //id of the last valid item in the list is fetched
          const lastItemId = sessionData[0].listSubModels[listLength - 2]?.id
          dispatch({
            type: 'UPDATE_CRISPR_CURRENT_INDEX',
            data: lastItemId,
          })
        }
      }
    }
    return function cleanupError() {
      dispatch({ type: 'SET_PAGE_ERROR', data: '' })
      dispatch({
        type: 'UPDATE_ERRORS',
        data: [],
      })
      //dispatch and prop method 'setErrorMsg' set for maximum line item limit
      dispatch({ type: 'IS_DISPLAY_ERROR', data: false })
      setErrorMsg('')
    }
  }, [])

  const tableColumns = [
    {
      headElement: <FormattedMessage {...messages.NAME} />,
    },
    {
      headElement: <FormattedMessage {...messages.CONF_BASES} />,
    },
    {
      headElement: <FormattedMessage {...messages.CONF_SEQUENCE_LABEL} />,
    },
    {
      headElement: <FormattedMessage {...messages.GENE_ID} />,
    },
    {
      headElement: <FormattedMessage {...messages.GENE_SYMBOL} />,
    },
    {
      headElement: <FormattedMessage {...messages.DELETE} />,
    },
  ]

  return (
    <div>
      {errorMessages?.length &&
        errorMessages.map((error, index) =>
          error.hasError ? (
            <GlobalAlert
              key={index}
              severity="error"
              className={classes.errorAlert}
            >
              {error.errorMessage}
            </GlobalAlert>
          ) : null
        )}
      <div>
        <div
          className={clsx(
            classes.sequenceEntery,
            errorMessages?.length &&
              errorMessages[0]?.hasError &&
              classes.hasErrorMessages
          )}
        >
          <FormattedMessage {...messages.CONF_SEQUENCE_ENTRY} />
        </div>
        <div className={classes.requiredField}>
          * <FormattedMessage {...messages.REQUIRED_FIELDS} />
        </div>
        <SequenceEntryItem
          productDetails={productDetails}
          disable={disable}
          instructions={instructions}
          setErrorMsg={setErrorMsg}
        />
      </div>

      {crisprSequenceList ? (
        <>
          <SequenceList
            productDetails={productDetails}
            setDisable={setDisable}
            setErrorMsg={setErrorMsg}
          />
        </>
      ) : (
        <>
          <div className={classes.sequwnceListHeader}>
            <FormattedMessage {...messages.CONF_CRISPR_SEQUENCE_LIST} />
          </div>
          <div className={classes.startTypingMsg}>
            <FormattedMessage
              id="EMPTY_SEQUENCE_PREVIEW_TEXT"
              defaultMessage="Start typing sequence above to see the preview."
            />
          </div>
          <DataTable data={[]} columns={tableColumns} />
        </>
      )}
    </div>
  )
}

export default ManualSearch
